import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { User } from 'firebase/auth';
import { initFirebase, onAuthStateChanged } from '../utils/firebase';
import { setDevToken } from '../config/apiConfig';
import { disconnectCable } from '../network/CableClient';
import { resetSessionChannelHub } from '../network/sessionChannelHub';
import { clearIdentityCache } from '../hooks/useIdentity';
import { clearRegistrationStatusCache } from '../hooks/useRegistrationStatus';
import { clearPendingSignup } from '../utils/pendingSignup';
import { shouldResetRealtimeIdentity, computeIdentityKey } from './authLifecycle';

/**
 * Central realtime teardown for a logout / account-identity change. Order:
 * purge the session-channel hub FIRST (unsubscribe on the still-live consumer),
 * then disconnect the consumer, then clear the identity/registration caches and
 * any pending signup. Keeps ALL identity-bound state invalidated on an identity
 * change (F2 + N1).
 *
 * Each step is isolated so this function NEVER throws: it runs inside the
 * onAuthStateChanged callback BEFORE the auth-state commit (setUser/setLoading),
 * and a throw there would skip the commit — stranding `isLoggedIn` at its old
 * value. That is exactly what left the Sidebar/Drawer logout button stuck at
 * "Signing out…": a fresh signup creates a cable consumer, and an immediate
 * logout tears it down while it may still be mid-creation (getCableConsumer
 * throws "aborted — disconnected during setup"), which previously aborted the
 * callback before `setUser(null)`. Isolating the steps guarantees logout (and
 * every identity transition) always commits, and still runs every remaining
 * teardown step even if one fails.
 */
// All five teardown steps are SYNCHRONOUS (each returns void: resetSessionChannelHub,
// disconnectCable, clearIdentityCache, clearRegistrationStatusCache, clearPendingSignup),
// so a plain try/catch fully covers their failure mode. The thenable check is a
// defensive guard only: if a step is ever made async, its rejection is swallowed
// here too (logged, not rethrown) so it can neither surface as an unhandled
// rejection nor, via the caller, strand the auth-state commit. We do NOT turn any
// teardown API async — this just tolerates one if it ever happens.
function runTeardownStep(name: string, step: () => unknown): void {
  try {
    const result = step();
    if (result && typeof (result as PromiseLike<unknown>).then === 'function') {
      (result as PromiseLike<unknown>).then(
        undefined,
        (e) => console.error(`[AuthContext] realtime teardown step "${name}" rejected (auth state still committed):`, e),
      );
    }
  } catch (e) {
    console.error(`[AuthContext] realtime teardown step "${name}" failed (auth state still committed):`, e);
  }
}

function resetRealtimeIdentity(): void {
  runTeardownStep('resetSessionChannelHub', () => resetSessionChannelHub());
  runTeardownStep('disconnectCable',        () => disconnectCable());
  runTeardownStep('clearIdentityCache',     () => clearIdentityCache());
  runTeardownStep('clearRegistrationStatusCache', () => clearRegistrationStatusCache());
  // Drop any half-finished email-registration credentials on logout/account
  // switch so account B can never inherit account A's pending signup state.
  runTeardownStep('clearPendingSignup',     () => clearPendingSignup());
}

interface AuthContextValue {
  user:             User | null;
  isLoggedIn:       boolean;
  loading:          boolean;
  loginWithDevToken: (token: string, email?: string) => void;
  /** Email-prefix of the dev-bypass login. Empty string in production. */
  devDisplayName:   string;
  /**
   * Stable key for the CURRENTLY authenticated identity — Firebase uid, or a
   * `dev:<token>` marker for the dev-token path, or null when logged out.
   * Consumers that must RE-INITIALIZE on an account change (FriendProvider,
   * useIdentity) key their effects on this rather than the `isLoggedIn` boolean,
   * so a direct A→B switch (truthy→truthy) still re-subscribes/re-fetches (F2).
   */
  identityKey:      string | null;
}

const AuthContext = createContext<AuthContextValue>({
  user:              null,
  isLoggedIn:        false,
  loading:           true,
  loginWithDevToken: () => {},
  devDisplayName:    '',
  identityKey:       null,
});

// Dev-only session persistence: the dev-token login previously lived only in
// React state, so ANY full page load (typing a URL, F5) silently logged the
// developer out — direct-URL routes could never be reached locally.
// sessionStorage (NOT localStorage) keeps the dev session tab-scoped: closing
// the tab still ends it. Guarded by import.meta.env.DEV at every touch point;
// production builds never read or write these keys.
const DEV_TOKEN_SS_KEY = 'dev_auth_token';
const DEV_NAME_SS_KEY  = 'dev_auth_name';

function restoreDevSession(key: string): string {
  if (!import.meta.env.DEV) return '';
  try { return sessionStorage.getItem(key) || ''; } catch { return ''; }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user,           setUser]          = useState<User | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [devToken,       setDevTokenState]  = useState<string>(
    import.meta.env.DEV
      ? (import.meta.env.VITE_DEV_TOKEN as string || restoreDevSession(DEV_TOKEN_SS_KEY))
      : ''
  );
  const [devDisplayName, setDevDisplayName] = useState<string>(() => restoreDevSession(DEV_NAME_SS_KEY));

  // A restored dev session must also re-arm the API client's bearer token
  // (module state in apiConfig is lost on reload alongside the React state).
  useEffect(() => {
    if (import.meta.env.DEV && devToken) setDevToken(devToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Previous Firebase uid, so we can detect logout (A → null) and account
  // switch (A → B) and tear down the shared realtime identity centrally (F2).
  const prevUidRef = useRef<string | null>(null);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
    if (!apiKey) {
      setLoading(false);
      return;
    }
    try {
      initFirebase();
      const unsubscribe = onAuthStateChanged((u) => {
        const newUid = u?.uid ?? null;
        const prevUid = prevUidRef.current;
        // Only when we are ABANDONING a real previous identity (logout or switch
        // to a different account) — never on the initial null → A login, which
        // must NOT disconnect the consumer FriendProvider is about to create.
        if (shouldResetRealtimeIdentity(prevUid, newUid)) {
          resetRealtimeIdentity(); // purge hub + drop consumer/token/timer + identity cache
        }
        prevUidRef.current = newUid;
        setUser(u);
        setLoading(false);
      });
      return unsubscribe;
    } catch {
      setLoading(false);
    }
  }, []);

  function loginWithDevToken(token: string, email?: string): void {
    if (!import.meta.env.DEV) return;
    // Dev account switch bypasses Firebase's onAuthStateChanged, so tear down the
    // previous identity's realtime state here too (F2 + N1, dev parity).
    resetRealtimeIdentity();
    setDevToken(token);
    setDevTokenState(token);
    const name = email?.split('@')[0] ?? '';
    setDevDisplayName(name);
    try {
      sessionStorage.setItem(DEV_TOKEN_SS_KEY, token);
      sessionStorage.setItem(DEV_NAME_SS_KEY, name);
    } catch { /* storage unavailable — session stays memory-only as before */ }
    setLoading(false);
  }

  // In production import.meta.env.DEV is always false, so devToken never counts.
  const isLoggedIn = !!user || (import.meta.env.DEV && !!devToken);

  // Changes on every authenticated-identity change, including a direct A→B switch
  // (Firebase or dev-token) that leaves the isLoggedIn boolean unchanged.
  const identityKey: string | null = computeIdentityKey(user?.uid, devToken, import.meta.env.DEV);

  return (
    <AuthContext.Provider value={{ user, isLoggedIn, loading, loginWithDevToken, devDisplayName, identityKey }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
